import { orderBy } from 'lodash';
import React, { Fragment, useState } from 'react';
import { ReportTable } from '../../shared/components/ReportTable';
import { ReportInfoPanel } from '../../shared/components/ReportInfoPanel';
import moment from 'moment';
import { useQuery } from '@apollo/client';
import ApolloError from '../../shared/components/ApolloError';
import useMddapPracticeAreas from '../../shared/hooks/useMddapPracticeAreas';
import GET_ORG_UNITS_AGGREGATE_REPORT from '../queries/getOrgUnitAggregateReportQuery';
import {
  APPRAISAL_SPONSOR,
  APPRAISAL_TEAM_LEAD
} from '../../../constants/appraisal-team-member-types';
import UnitDetailsBox from '../../shared/components/UnitDetailsBox';
import { isUserFDATier } from '../../shared/services/checkUserTierService';
import PageInstructions from '../../shared/components/PageInstructions';
import { toHumanReadableDate } from '../../shared/services/dateService';
import cx from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';
import { pageUrlService } from '@shared/services/pageUrlService';

/**
 * @typedef {object} AggregateAppraisalReportProps
 * @property {number} orgUnitId
 * @property {string} tier
 */

/**
 * @param {AggregateAppraisalReportProps} props
 */
const AggregateAppraisalReport = props => {
  const { orgUnitId, tier } = props;

  const location = useLocation();
  const orgUnitSelectedAppraisalId = location.state
    ? location.state.orgUnitSelectedAppraisalId
    : null;

  const [selectedAppraisalId, setSelectedAppraisalId] = useState(orgUnitSelectedAppraisalId);

  const {
    loading: mddapPracticeAreasLoading,
    data: mddapPas,
    error: mddapPracticeAreasError
  } = useMddapPracticeAreas();

  const {
    data: aggregateReportData,
    error,
    loading: aggregateReportLoading
  } = useQuery(GET_ORG_UNITS_AGGREGATE_REPORT, {
    variables: {
      id: orgUnitId
    }
  });

  const navigate = useNavigate();

  const appraisalOrgUnits = aggregateReportData
    ? orderBy(
        aggregateReportData.aggregateOrgUnitReport.AppraisalOrgUnits,
        ou => [ou.Appraisal.ConductAppraisalEndDate, ou.Appraisal.Id],
        ['desc', 'asc']
      )
    : [];

  const handleClickOutside = () => {
    setSelectedAppraisalId(null);
  };

  const handleOnYearHeaderClick = (event, appraisalId) => {
    event.stopPropagation();
    setSelectedAppraisalId(appraisalId);
  };

  const handleSelectUnit = orgUnitId => {
    navigate(pageUrlService.getAggregateAppraisalReportPageUrl(orgUnitId), {
      state: {
        orgUnitSelectedAppraisalId: selectedAppraisalId
      }
    });
  };

  if (error || mddapPracticeAreasError) {
    return <ApolloError />;
  }

  const buildYears = () => {
    const headers = appraisalOrgUnits.map(appraisalOu => {
      const apprYear = moment(appraisalOu.Appraisal.ConductAppraisalEndDate).format('YYYY');

      return (
        <th
          colSpan={aggregateReportData.isPercentageVisible ? 2 : 1}
          className={cx('report-table__cell--centered', {
            'report-table__cell--highlight': selectedAppraisalId === appraisalOu.Appraisal.Id
          })}
          onClick={e => handleOnYearHeaderClick(e, appraisalOu.Appraisal.Id)}
          key={appraisalOu.Appraisal.Id}>
          Year {appraisalOu.AppraisalProgramYear} ({apprYear})
        </th>
      );
    });

    return (
      <>
        <th className='report-table__border-right' />
        {headers}
      </>
    );
  };

  const buildColumns = () => {
    let groupNumber = 0;
    return appraisalOrgUnits.map(unit => (
      <Fragment key={unit.Id}>
        <th
          className={cx('report-table__cell--centered-below', {
            'report-table__border-left': groupNumber++ >= 1
          })}>
          Practice Group Level
        </th>
        {aggregateReportData.isPercentageVisible && (
          <th className='report-table__cell--centered-below' data-testid='table-percentage-header'>
            Percent
          </th>
        )}
      </Fragment>
    ));
  };

  const buildPARows = () => {
    if (aggregateReportLoading) {
      return [];
    }

    return mddapPas.map(pa => {
      let groupNumber = 0;
      const rowFragments = appraisalOrgUnits.map(appraisalOu => {
        const targetPa = appraisalOu.AppraisalOrgUnitTargetPracticeAreas.find(
          appraisalPracticeArea =>
            pa.MergedPracticeAreaIds.includes(appraisalPracticeArea.PracticeArea.Id)
        );
        const percentageDisplay =
          targetPa && targetPa.AggregatePercentage !== null ? targetPa.AggregatePercentage : '';
        return (
          <Fragment key={groupNumber + '-' + pa.InternalId}>
            <td
              className={cx('report-table__cell--centered-below', {
                'report-table__border-left': groupNumber++ >= 1
              })}>
              {targetPa ? targetPa.PracticeGroup.Level : 'N/A'}
            </td>
            {aggregateReportData.isPercentageVisible && (
              <td className='report-table__cell--centered-below' data-testid='table-percentage-row'>
                {percentageDisplay}%
              </td>
            )}
          </Fragment>
        );
      });
      return (
        <tr key={pa.InternalId} className='report-table__row--sticky-column'>
          <td className='report-table__border-right'>
            {pa.Name} ({pa.Acronym})
          </td>
          {rowFragments}
        </tr>
      );
    });
  };

  const renderAppraisalInfoBox = () => {
    if (!selectedAppraisalId) {
      return <p className='no-margin'>Select a Program Year for more info.</p>;
    }

    const appraisalOu = appraisalOrgUnits.find(x => x.Appraisal.Id === selectedAppraisalId);
    const appraisalOuInfo = getAppraisalOuInfoForMoreInfoBox(appraisalOu);

    return (
      <div>
        <UnitDetailsBox
          data={{ ...appraisalOuInfo }}
          rationaleDocument={appraisalOuInfo.reappraisalRationaleDocument}
          showAppraisalDetails={true}
          showUnitDetails={false}
          handleSelectUnit={handleSelectUnit}
        />
      </div>
    );
  };

  const description =
    'Summary Appraisal Results year over year, including Practice Areas (PAs) ' +
    'appraised and the PA aggregate percentages. Select a Program Year for more info.';

  return (
    <div onClick={handleClickOutside} className='aggragate-appraisal'>
      <PageInstructions
        title='Aggregate Appraisal Report'
        description={description}
        fdaShareWarning={!isUserFDATier(tier)}
      />
      <div className='report__body aggregate-appraisal-report'>
        <div className='report-info-panels'>
          <ReportInfoPanel panelName='report-info-panel__small' loading={aggregateReportLoading}>
            <UnitDetailsBox
              data={
                aggregateReportData
                  ? getOrgUnitInfoForMoreInfoBox(
                      appraisalOrgUnits[0].Appraisal,
                      aggregateReportData.aggregateOrgUnitReport
                    )
                  : {}
              }
              showAppraisalDetails={false}
              showUnitDetails={true}
            />
          </ReportInfoPanel>
          <ReportInfoPanel
            panelName='report-info-panel__large'
            loading={Boolean(selectedAppraisalId) && aggregateReportLoading}>
            {renderAppraisalInfoBox()}
          </ReportInfoPanel>
        </div>
        <div className='report-wrapper'>
          <ReportTable
            loading={aggregateReportLoading || mddapPracticeAreasLoading}
            headers={
              <>
                <tr className='report-table__row--sticky-first-row report-table__row--sticky-column'>
                  {buildYears()}
                </tr>
                <tr className='report-table__row--sticky-second-row report-table__row--sticky-column'>
                  <th className='report-table__border-right'>Practice Area</th>
                  {buildColumns()}
                </tr>
              </>
            }
            rows={buildPARows()}
          />
        </div>
      </div>
    </div>
  );
};

const getAppraisalOuInfoForMoreInfoBox = appraisalOu => {
  if (!appraisalOu) {
    return {};
  }
  const appraisal = appraisalOu.Appraisal;
  const teamLeadName = appraisal.AppraisalTeamMembers.find(x => x.Type === APPRAISAL_TEAM_LEAD)
    .Individual.Name;
  const appraisalDate = toHumanReadableDate(appraisal.ConductAppraisalEndDate);
  const appraisalID = appraisal.Id;
  const appraisalUnits = appraisal.AppraisalOrgUnits;
  const reappraisalRationaleDocument = appraisalOu.ReappraisalRationaleDocument;

  return {
    teamLeadName,
    appraisalDate,
    appraisalID,
    appraisalUnits,
    reappraisalRationaleDocument
  };
};

const getOrgUnitInfoForMoreInfoBox = (latestAppraisal, orgUnit) => {
  const address = [orgUnit.Address1, orgUnit.Address2, orgUnit.City, orgUnit.State, orgUnit.ZipCode]
    .filter(Boolean)
    .join(', ');

  const OHTs = orgUnit.MddapOfficeOfHealthTechnologies.map(x =>
    x.OfficeOfHealthTechnology.Value.split(':')[0].replace('OHT', '')
  ).join(', ');
  const enrollmentDate = toHumanReadableDate(orgUnit.EnrollmentDate);
  const sponsorName = latestAppraisal.AppraisalTeamMembers.find(x => x.Type === APPRAISAL_SPONSOR)
    .Individual.Name;

  return {
    location: address,
    OHTs: OHTs,
    enrollmentDate: enrollmentDate,
    sponsorName: sponsorName,
    FEI: orgUnit.Fei
  };
};

export default AggregateAppraisalReport;
